import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DropdownMenu, { ButtonTextWrapper, ChevronIcon, OpenerButton } from 'components/common/dropdown_menu_v2';
import ExpandableTextArea from 'components/common/expandable_text_area';
import Menu from 'components/common/menu';
import MenuIcon from 'components/lib/icons/menu_icon';
import TeamAssistIcon from 'components/lib/icons/team_assist_icon';

export function TeamAssistInput({ dataAid, dataAidSend, disabled, onChange, onKeyDown, onSubmit, placeholder, value }) {
  const { t } = useTranslation();
  return (
    <InputWrapper>
      <StyledTextArea
        data-aid={dataAid}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder ?? t('Chat with Gladly AI')}
        value={value}
      />
      <SendButton data-aid={dataAidSend} disabled={disabled} onClick={onSubmit} type="button">
        <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '16px' }} />
      </SendButton>
    </InputWrapper>
  );
}

TeamAssistInput.propTypes = {
  dataAid: PropTypes.string,
  dataAidSend: PropTypes.string,
  disabled: PropTypes.bool,
  onKeyDown: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
};

// `placeholder` has no defaultProps entry on purpose: its default is translated
// inside the component so it re-renders on a language change.
TeamAssistInput.defaultProps = {
  dataAid: 'teamAssistInput',
  dataAidSend: 'teamAssistSendButton',
  disabled: false,
};

const InputWrapper = styled.div`
  position: relative;
`;

const StyledTextArea = styled(ExpandableTextArea)`
  background: ${p => p.theme.colors.gray100};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 24px;
  box-sizing: border-box;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: ${p => p.theme.fontSize.base};
  line-height: 20px;
  min-height: 48px;
  outline: none;
  padding: 13px 46px 13px 14px;
  width: 100%;
  &::placeholder {
    color: ${p => p.theme.colors.gray500};
  }
  &:focus {
    border-color: ${p => p.theme.colors.purple400};
  }
`;

const SendButton = styled.button`
  align-items: center;
  background: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.purple400)};
  border: none;
  border-radius: 50%;
  color: ${p => p.theme.colors.white};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  height: 32px;
  justify-content: center;
  position: absolute;
  right: 8px;
  bottom: 14px;
  width: 32px;
  &:hover {
    background: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.purple500)};
  }
`;

export const TeamAssistActionsDropdown = styled(DropdownMenu)`
  ${ChevronIcon} {
    display: none;
  }

  ${ButtonTextWrapper} {
    align-items: center;
    display: inline-flex;
    justify-content: center;
    width: auto;
  }
`;

export const TeamAssistActionsMenuOpener = styled(OpenerButton).attrs({
  'aria-haspopup': 'menu',
  'aria-label': 'Team Assist actions',
})`
  cursor: pointer;
  min-height: auto;
  padding: 4px 8px;

  &&,
  &&:active,
  &&:focus,
  &&:hover {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .menuIcon {
    fill: ${p => p.theme.colors.gray800};
    height: 16px;
    width: 4px;
  }

  &&:hover {
    .menuIcon {
      fill: ${p => p.theme.colors.green400};
    }
  }
`;

export const TeamAssistActionsMenuIcon = styled(MenuIcon)`
  color: currentColor;
`;

export const TeamAssistModalActionsMenu = styled(Menu)`
  z-index: 40;
`;

export const TeamAssistPanelActionsMenu = styled(Menu)`
  z-index: 30;
`;

export const CannedResponseButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

export const CannedResponseButton = styled.button`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 16px;
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.small};
  line-height: 1.3;
  padding: 6px 12px;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background: ${p => p.theme.colors.gray100};
    border-color: ${p => p.theme.colors.gray400};
  }
`;

export const StyledPanel = styled.div`
  box-shadow: ${p => p.theme.boxShadow.small};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  outline: none;
  width: 100%;
  z-index: 3;
`;

export const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  padding: 16px;
`;

export const StyledHeaderIcon = styled(TeamAssistIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
`;

export const HeaderText = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.purple400};
  display: flex;
  font-size: var(--font-size-medium);
  font-weight: ${p => p.theme.fontWeight.medium};
  gap: 6px;
`;

export const HeaderSubtitle = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: var(--font-size-reduced);
  line-height: 1.2;
  margin-left: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 160px;
  margin-right: 2px;
  min-width: 0;
`;

export const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

export const MessagesArea = styled.div`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: none;
  padding: 16px;
`;

export const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 24px 16px;
  text-align: center;
`;

export const EmptyStateIconWrapper = styled.div`
  color: ${p => p.theme.colors.purple400};
  margin-bottom: 16px;
  opacity: 0.85;
`;

export const EmptyStateText = styled.p`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  margin: 0;
  max-width: 260px;
`;

export const InputArea = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  padding: 12px 16px;
`;
